import type {
  PublicCapabilityProjection,
  PublicCapabilityValidationLabel,
} from "@dj/convex-api";

import type { ProfileExpertiseIconTone, ProfileExpertiseOverviewModel } from "./profileExpertiseOverview";
import { skillIconTone, skillIndustryTags } from "./profileExpertiseOverview";

export type ProfileExpertiseTaxonomyNode = Readonly<{
  taxonomyKey: string;
  nodeKind: "skill" | "industry" | "mission_category";
  displayName: string;
}>;

/** Mirrors governed backend skill/industry taxonomy for owner editor UX. */
export const PROFILE_EXPERTISE_SKILL_LIBRARY: readonly ProfileExpertiseTaxonomyNode[] = [
  { taxonomyKey: "aerial_mapping_surveying", nodeKind: "skill", displayName: "Aerial Mapping & Surveying" },
  { taxonomyKey: "mapping_2d", nodeKind: "skill", displayName: "2D Mapping" },
  { taxonomyKey: "mapping_3d", nodeKind: "skill", displayName: "3D Mapping" },
  { taxonomyKey: "thermal_inspection", nodeKind: "skill", displayName: "Thermal Inspection" },
  { taxonomyKey: "infrastructure_inspection", nodeKind: "skill", displayName: "Infrastructure Inspection" },
  { taxonomyKey: "roof_inspection", nodeKind: "skill", displayName: "Roof Inspection" },
  { taxonomyKey: "construction_monitoring", nodeKind: "skill", displayName: "Construction Monitoring" },
  { taxonomyKey: "agriculture_crop_scouting", nodeKind: "skill", displayName: "Agriculture & Crop Scouting" },
  { taxonomyKey: "3d_modeling_photogrammetry", nodeKind: "skill", displayName: "3D Modeling & Photogrammetry" },
  { taxonomyKey: "video_photo_documentation", nodeKind: "skill", displayName: "Video & Photo Documentation" },
] as const;

export const PROFILE_EXPERTISE_INDUSTRY_LIBRARY: readonly ProfileExpertiseTaxonomyNode[] = [
  { taxonomyKey: "real_estate", nodeKind: "industry", displayName: "Real Estate" },
  { taxonomyKey: "construction", nodeKind: "industry", displayName: "Construction" },
  { taxonomyKey: "energy", nodeKind: "industry", displayName: "Energy" },
  { taxonomyKey: "agriculture", nodeKind: "industry", displayName: "Agriculture" },
  { taxonomyKey: "infrastructure", nodeKind: "industry", displayName: "Infrastructure" },
  { taxonomyKey: "insurance", nodeKind: "industry", displayName: "Insurance" },
  { taxonomyKey: "government", nodeKind: "industry", displayName: "Government" },
  { taxonomyKey: "events", nodeKind: "industry", displayName: "Events" },
  { taxonomyKey: "security", nodeKind: "industry", displayName: "Security" },
  { taxonomyKey: "public_safety", nodeKind: "industry", displayName: "Public Safety" },
  { taxonomyKey: "film_media", nodeKind: "industry", displayName: "Film and Media" },
  { taxonomyKey: "surveying_mapping", nodeKind: "industry", displayName: "Surveying and Mapping" },
  { taxonomyKey: "environmental", nodeKind: "industry", displayName: "Environmental" },
  { taxonomyKey: "other", nodeKind: "industry", displayName: "Other" },
] as const;

const industryLabelByKey = new Map(
  PROFILE_EXPERTISE_INDUSTRY_LIBRARY.map((node) => [node.taxonomyKey, node.displayName]),
);
industryLabelByKey.set("media", "Film and Media");

const skillLabelByKey = new Map(
  PROFILE_EXPERTISE_SKILL_LIBRARY.map((node) => [node.taxonomyKey, node.displayName]),
);

export type ProfileExpertiseEditorDraft = Readonly<{
  skillKeys: readonly string[];
  industryKeysBySkill: Readonly<Record<string, readonly string[]>>;
}>;

export type ProfileExpertiseEditorSkillRow = Readonly<{
  skillKey: string;
  name: string;
  iconTone: ProfileExpertiseIconTone;
  industryLabels: readonly string[];
}>;

export type ProfileExpertiseEditorLibrarySkill = Readonly<{
  skillKey: string;
  name: string;
  iconTone: ProfileExpertiseIconTone;
}>;

export type ProfileExpertiseEditorDiff = Readonly<{
  skillKeysToAdd: readonly string[];
  skillIdsToArchive: readonly string[];
  skillIndustries: readonly Readonly<{
    skillKey: string;
    industryKeys: readonly string[];
  }>[];
}>;

export type ProfileExpertiseEditorSourceRecords = Readonly<{
  skills: readonly Readonly<{ id: string; skillKey: string }>[];
  industryExpertise: readonly Readonly<{ id: string; industryKey: string; skillKey?: string }>[];
}>;

export type ProfileExpertiseOwnerCapabilityRecords = Readonly<{
  skills: readonly Readonly<{
    id: string;
    skillKey: string;
    validationStatus?: string;
    confirmationCount?: number;
  }>[];
  industryExpertise: readonly Readonly<{
    id: string;
    industryKey: string;
    skillKey?: string;
    confirmationCount?: number;
  }>[];
}>;

function mapOwnerSkillValidationStatus(status?: string): PublicCapabilityValidationLabel {
  if (status === "validated") return "validated";
  if (status === "confirmed") return "confirmed";
  return "listed";
}

export function buildOwnerCapabilityProjection(
  records: ProfileExpertiseOwnerCapabilityRecords,
  publicProjection?: PublicCapabilityProjection | null,
): PublicCapabilityProjection {
  const manualIndustryKeys = new Set(records.industryExpertise.map((entry) => entry.industryKey));
  const missionExpertise = (publicProjection?.expertise ?? []).filter(
    (entry) => (entry.missionCount ?? 0) > 0
      && !manualIndustryKeys.has(entry.publicExpertiseKey.replace(/^expertise:/, "")),
  );

  return {
    schemaVersion: "public_capability_projection.v1",
    sectionState: records.skills.length > 0 ? "listed" : "empty",
    renderable: records.skills.length > 0,
    generatedAt: publicProjection?.generatedAt ?? Date.now(),
    skills: records.skills.map((skill) => ({
      publicSkillKey: `skill:${skill.skillKey}`,
      name: profileExpertiseSkillKeyToLabel(skill.skillKey),
      validationStatus: mapOwnerSkillValidationStatus(skill.validationStatus),
      ...(skill.confirmationCount ? { confirmationCount: skill.confirmationCount } : {}),
    })),
    expertise: [
      ...records.industryExpertise.map((entry) => ({
        publicExpertiseKey: `expertise:${entry.industryKey}`,
        name: profileExpertiseIndustryKeyToLabel(entry.industryKey),
        sourceKind: "manual" as const,
        validationStatus: "listed" as const,
        ...(entry.confirmationCount ? { confirmationCount: entry.confirmationCount } : {}),
      })),
      ...missionExpertise,
    ],
  };
}

export function buildOwnerExpertiseOverviewModel(
  records: ProfileExpertiseOwnerCapabilityRecords,
  publicProjection?: PublicCapabilityProjection | null,
): ProfileExpertiseOverviewModel {
  const industryKeysBySkill = buildIndustryKeysBySkill(
    records.skills.map((skill) => skill.skillKey),
    records.industryExpertise,
  );
  const expertise = publicProjection?.expertise ?? [];

  const tiles = records.skills.map((skill) => {
    const industryLabels = (industryKeysBySkill[skill.skillKey] ?? [])
      .map(profileExpertiseIndustryKeyToLabel);
    const missionCount = missionCountForSkillFromExpertise(skill.skillKey, expertise);

    return {
      tileKey: `skill:${skill.skillKey}`,
      name: profileExpertiseSkillKeyToLabel(skill.skillKey),
      industries: industryLabels,
      iconTone: skillIconTone(skill.skillKey),
      ...(missionCount ? { missionCount } : {}),
      ...(skill.confirmationCount ? { confirmationCount: skill.confirmationCount } : {}),
      validationStatus: skill.validationStatus ?? "self_declared",
    };
  });

  const filterSet = new Set<string>(["All"]);
  for (const tile of tiles) {
    for (const tag of tile.industries) {
      filterSet.add(tag);
    }
  }

  return {
    filterOptions: sortExpertiseFilterOptions(filterSet),
    tiles,
  };
}

function missionCountForSkillFromExpertise(
  skillKey: string,
  expertise: readonly { publicExpertiseKey: string; missionCount?: number }[],
): number | undefined {
  const industryKeys = SKILL_MISSION_INDUSTRY_KEYS[skillKey];
  if (!industryKeys?.length) return undefined;

  let total = 0;
  for (const entry of expertise) {
    const entryKey = entry.publicExpertiseKey.replace(/^expertise:/, "");
    if (industryKeys.includes(entryKey)) {
      total += entry.missionCount ?? 0;
    }
  }
  return total > 0 ? total : undefined;
}

const SKILL_MISSION_INDUSTRY_KEYS: Record<string, readonly string[]> = {
  aerial_mapping_surveying: ["surveying_mapping"],
  mapping_2d: ["surveying_mapping"],
  mapping_3d: ["surveying_mapping", "real_estate"],
  thermal_inspection: ["energy"],
  infrastructure_inspection: ["infrastructure"],
  roof_inspection: ["infrastructure", "construction"],
  construction_monitoring: ["construction"],
  agriculture_crop_scouting: ["agriculture"],
  "3d_modeling_photogrammetry": ["real_estate"],
  video_photo_documentation: ["film_media"],
};

const EXPERTISE_FILTER_ORDER = [
  "All",
  "Construction",
  "Energy",
  "Agriculture",
  "Real Estate",
  "Media",
  "Infrastructure",
  "Inspection",
  "Survey",
] as const;

function sortExpertiseFilterOptions(options: Iterable<string>): string[] {
  const unique = [...new Set(options)];
  return unique.sort((left, right) => {
    const leftIndex = EXPERTISE_FILTER_ORDER.indexOf(left as typeof EXPERTISE_FILTER_ORDER[number]);
    const rightIndex = EXPERTISE_FILTER_ORDER.indexOf(right as typeof EXPERTISE_FILTER_ORDER[number]);
    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });
}

function distributeLegacyIndustryKeysToSkills(
  skillKeys: readonly string[],
  legacyIndustryKeys: readonly string[],
): Record<string, string[]> {
  const bySkill: Record<string, string[]> = Object.fromEntries(
    skillKeys.map((skillKey) => [skillKey, [] as string[]]),
  );
  const allLabels = PROFILE_EXPERTISE_INDUSTRY_LIBRARY.map((node) => node.displayName);

  for (const industryKey of legacyIndustryKeys) {
    const label = profileExpertiseIndustryKeyToLabel(industryKey);
    let assigned = false;
    for (const skillKey of skillKeys) {
      const associated = new Set(skillIndustryTags(skillKey, allLabels));
      if (associated.has(label)) {
        bySkill[skillKey]?.push(industryKey);
        assigned = true;
      }
    }
    if (!assigned && skillKeys[0]) {
      bySkill[skillKeys[0]]?.push(industryKey);
    }
  }

  return bySkill;
}

function buildIndustryKeysBySkill(
  skillKeys: readonly string[],
  industryExpertise: readonly Readonly<{ industryKey: string; skillKey?: string }>[],
): Record<string, string[]> {
  const bySkill: Record<string, string[]> = Object.fromEntries(
    skillKeys.map((skillKey) => [skillKey, [] as string[]]),
  );
  const legacyIndustryKeys: string[] = [];

  for (const entry of industryExpertise) {
    if (entry.skillKey && bySkill[entry.skillKey]) {
      if (!bySkill[entry.skillKey].includes(entry.industryKey)) {
        bySkill[entry.skillKey].push(entry.industryKey);
      }
      continue;
    }
    legacyIndustryKeys.push(entry.industryKey);
  }

  if (legacyIndustryKeys.length > 0) {
    const distributed = distributeLegacyIndustryKeysToSkills(skillKeys, legacyIndustryKeys);
    for (const skillKey of skillKeys) {
      for (const industryKey of distributed[skillKey] ?? []) {
        if (!bySkill[skillKey].includes(industryKey)) {
          bySkill[skillKey].push(industryKey);
        }
      }
    }
  }

  return bySkill;
}

function industryKeysForSkill(
  draft: ProfileExpertiseEditorDraft,
  skillKey: string,
): readonly string[] {
  return draft.industryKeysBySkill[skillKey] ?? [];
}

export function profileExpertiseIndustryKeyToLabel(industryKey: string): string {
  return industryLabelByKey.get(industryKey) ?? industryKey.replace(/_/g, " ");
}

export function profileExpertiseSkillKeyToLabel(skillKey: string): string {
  return skillLabelByKey.get(skillKey) ?? skillKey.replace(/_/g, " ");
}

export function profileExpertiseIndustryLabelToKey(label: string): string | undefined {
  if (label.toLowerCase() === "media") return "film_media";
  for (const node of PROFILE_EXPERTISE_INDUSTRY_LIBRARY) {
    if (node.displayName.toLowerCase() === label.toLowerCase()) {
      return node.taxonomyKey;
    }
  }
  return undefined;
}

export function seedProfileExpertiseEditorDraft(
  records: ProfileExpertiseEditorSourceRecords,
): ProfileExpertiseEditorDraft {
  const skillKeys = records.skills.map((skill) => skill.skillKey);
  return {
    skillKeys,
    industryKeysBySkill: buildIndustryKeysBySkill(skillKeys, records.industryExpertise),
  };
}

export function buildProfileExpertiseEditorSkillRows(
  draft: ProfileExpertiseEditorDraft,
): ProfileExpertiseEditorSkillRow[] {
  return draft.skillKeys.map((skillKey) => ({
    skillKey,
    name: profileExpertiseSkillKeyToLabel(skillKey),
    iconTone: skillIconTone(skillKey),
    industryLabels: industryKeysForSkill(draft, skillKey).map(profileExpertiseIndustryKeyToLabel),
  }));
}

export function listProfileExpertiseLibrarySkills(
  draft: ProfileExpertiseEditorDraft,
): ProfileExpertiseEditorLibrarySkill[] {
  const active = new Set(draft.skillKeys);
  return PROFILE_EXPERTISE_SKILL_LIBRARY
    .filter((node) => !active.has(node.taxonomyKey))
    .map((node) => ({
      skillKey: node.taxonomyKey,
      name: node.displayName,
      iconTone: skillIconTone(node.taxonomyKey),
    }));
}

export function listProfileExpertiseIndustryPickerOptions(
  skillKey: string,
  draft: ProfileExpertiseEditorDraft,
): readonly ProfileExpertiseTaxonomyNode[] {
  const activeForSkill = new Set(industryKeysForSkill(draft, skillKey));

  return PROFILE_EXPERTISE_INDUSTRY_LIBRARY.filter((node) => !activeForSkill.has(node.taxonomyKey));
}

export function buildExpertiseCapabilityRecordsFromDraft(
  draft: ProfileExpertiseEditorDraft,
  sourceRecords: ProfileExpertiseEditorSourceRecords,
): ProfileExpertiseOwnerCapabilityRecords {
  const skillsByKey = new Map(sourceRecords.skills.map((skill) => [skill.skillKey, skill]));

  return {
    skills: draft.skillKeys.map((skillKey) => {
      const existing = skillsByKey.get(skillKey);
      return {
        id: existing?.id ?? `draft-skill:${skillKey}`,
        skillKey,
      };
    }),
    industryExpertise: draft.skillKeys.flatMap((skillKey) => (
      (draft.industryKeysBySkill[skillKey] ?? []).map((industryKey) => ({
        id: `draft-industry:${skillKey}:${industryKey}`,
        industryKey,
        skillKey,
      }))
    )),
  };
}

export function diffProfileExpertiseEditor(
  baseline: ProfileExpertiseEditorDraft,
  draft: ProfileExpertiseEditorDraft,
  records: ProfileExpertiseEditorSourceRecords,
): ProfileExpertiseEditorDiff {
  const baselineSkills = new Set(baseline.skillKeys);
  const draftSkills = new Set(draft.skillKeys);

  return {
    skillKeysToAdd: draft.skillKeys.filter((skillKey) => !baselineSkills.has(skillKey)),
    skillIdsToArchive: records.skills
      .filter((skill) => baselineSkills.has(skill.skillKey) && !draftSkills.has(skill.skillKey))
      .map((skill) => skill.id),
    skillIndustries: draft.skillKeys.map((skillKey) => ({
      skillKey,
      industryKeys: [...(draft.industryKeysBySkill[skillKey] ?? [])],
    })),
  };
}

export function draftsEqualProfileExpertiseEditor(
  left: ProfileExpertiseEditorDraft,
  right: ProfileExpertiseEditorDraft,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}
